import { AdvertiserCategory, STORE_SETUP_LIMITS, type StoreSetupRequest, type StoreSetupReuse, type StoreSetupStatus, type StoreSetupView, type StoreThemeId } from "@townsy/contracts";
import { isApiError } from "../api/client";
import { CATEGORY_LABELS } from "../advertise/advertiserRules";
import { checkDestinationUrl, isHexColor, suggestMonogram } from "../advertise/creativeModel";
import type { UploadState } from "../advertise/uploadMachine";

/**
 * "Your store" on one screen (ADR-0020, founder feedback 2026-10-14): the form's rules and its words. Pure, so they are
 * unit-tested without a DOM. The server validates everything again and decides what goes live.
 *
 * Words: the owner never reads "approved", "rejected", "creative", "placement" or "advertiser profile" here. The status
 * is one of "Live", "Checking your design… (usually under a minute)", "Waiting for a Townsy reviewer" or
 * "Needs a change: <reason>" (plus "Not set up yet" for a new store).
 */

export interface StoreSetupForm {
  businessName: string;
  description: string;
  website: string;
  category: AdvertiserCategory | "";
  /** The logo kept from the saved form (a new upload replaces it once ready). Null = name-only sign. */
  logo: { uploadId: string; previewUrl: string | null } | null;
  brandColor: string;
  signTitle: string;
  signMessage: string;
  interiorTheme: StoreThemeId;
  /** ADR-0031: the form was filled from the owner's own design of an earlier store ("Re-use your store design"). */
  reusedFrom?: { creativeId: string; fromAddress: string; businessName: string; accentColor: string; offers: number } | null;
}

export type StoreFormField = "businessName" | "description" | "website" | "category" | "logo" | "brandColor" | "signTitle" | "signMessage";
export type StoreFormErrors = Partial<Record<StoreFormField, string>>;

export const STORE_CATEGORY_HINT = "Required by Townsy's Advertiser Policy: it tells the reviewer what kind of business this is.";

/** Sign colours offered as one tap (any colour can still be typed). */
export const STORE_BRAND_SWATCHES = ["#2851D9", "#1E8A5A", "#C2410C", "#B91C1C", "#7C3AED", "#0F766E", "#1B1F2A", "#D99A2B"] as const;

/** The categories in the order the select shows them (the two restricted ones last, labelled). */
export const STORE_CATEGORY_OPTIONS: ReadonlyArray<{ value: AdvertiserCategory; label: string }> = AdvertiserCategory.options.map((value) => ({
  value,
  label: CATEGORY_LABELS[value].replace(" (restricted)", " (needs a Townsy decision)"),
}));

export function formFromView(v: StoreSetupView): StoreSetupForm {
  return {
    businessName: v.businessName,
    description: v.description,
    website: v.website ?? "",
    category: v.category ?? "",
    logo: v.logo,
    brandColor: v.brandColor.toUpperCase(),
    signTitle: v.sign.title,
    signMessage: v.sign.message,
    interiorTheme: v.interiorTheme,
  };
}

// ---------------------------------------------------------------- "Re-use your store design" (ADR-0031)

/** "Re-use your store design from Harbor Kite Co (3 Harbor Row)?" */
export function storeReuseQuestion(reuse: Pick<StoreSetupReuse, "businessName" | "fromAddress">): string {
  return `Re-use your store design from ${reuse.businessName} (${reuse.fromAddress})?`;
}

/**
 * One press of "Re-use this design": the screen filled in from the owner's own earlier store (name, category,
 * description, website, logo, sign colour, the sign inside and the interior style). Saved unchanged it goes on this store
 * at once (it was already checked); anything changed is checked first.
 */
export function formFromReuse(v: StoreSetupView): StoreSetupForm {
  const r = v.reuse;
  if (!r) return formFromView(v);
  return {
    businessName: r.businessName,
    description: r.description,
    website: r.website ?? "",
    category: r.category ?? "",
    logo: r.logo,
    brandColor: r.brandColor.toUpperCase(),
    signTitle: r.sign.title || v.sign.title,
    signMessage: r.sign.message || v.sign.message,
    interiorTheme: r.interiorTheme,
    reusedFrom: { creativeId: r.creativeId, fromAddress: r.fromAddress, businessName: r.businessName, accentColor: r.accentColor, offers: r.offers },
  };
}

/** The re-used design is still exactly as it was (it then goes on the store at once; the server decides again). */
export function reusedDesignUnchanged(f: StoreSetupForm, v: Pick<StoreSetupView, "reuse">, newLogoUploadId: string | null = null): boolean {
  const r = v.reuse;
  if (!f.reusedFrom || !r || r.creativeId !== f.reusedFrom.creativeId) return false;
  const site = checkDestinationUrl(f.website);
  const website = site.ok && !site.empty ? site.normalized : null;
  return (
    f.businessName.trim() === r.businessName &&
    f.description.trim() === r.description &&
    website === (r.website ?? null) &&
    f.category === (r.category ?? "") &&
    (newLogoUploadId ?? f.logo?.uploadId ?? null) === (r.logo?.uploadId ?? null) &&
    f.brandColor.toUpperCase() === r.brandColor.toUpperCase()
  );
}

/** What the filled-in screen says about the Save (one line). */
export function reuseNote(f: StoreSetupForm, v: Pick<StoreSetupView, "reuse" | "quickCheck">, newLogoUploadId: string | null = null): string {
  if (!f.reusedFrom) return "";
  const from = `Filled in from your store at ${f.reusedFrom.fromAddress}.`;
  const then = f.reusedFrom.offers > 0 ? " Then you can re-use your offers from there too." : "";
  if (reusedDesignUnchanged(f, v, newLogoUploadId)) return `${from} It was already checked, so Save puts it on this store right away.${then}`;
  return `${from} You changed it, so ${v.quickCheck ? "it gets a quick check first (usually under a minute)" : "a Townsy reviewer looks at it first"}.${then}`;
}

const CONTROL_RE = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F​-‏‪-‮⁦-⁩﻿]/;
const chars = (s: string) => Array.from(s).length;

export interface StoreFormContext {
  /** A new logo is uploading or being checked. */
  logoBusy: boolean;
  /** The store already has an in-store sign (it can be changed, not removed). */
  hasSign: boolean;
}

export function validateStoreForm(f: StoreSetupForm, ctx: StoreFormContext): StoreFormErrors {
  const e: StoreFormErrors = {};
  const name = f.businessName.trim();
  if (!name) e.businessName = "Enter your business name.";
  else if (chars(name) > STORE_SETUP_LIMITS.businessName) e.businessName = `Use at most ${STORE_SETUP_LIMITS.businessName} characters.`;
  else if (CONTROL_RE.test(name)) e.businessName = "Remove hidden or control characters.";
  if (chars(f.description) > STORE_SETUP_LIMITS.description) e.description = `Use at most ${STORE_SETUP_LIMITS.description} characters.`;
  if (!f.category) e.category = "Choose a category.";
  const site = checkDestinationUrl(f.website);
  if (!site.ok) e.website = site.message;
  if (ctx.logoBusy) e.logo = "Wait until your logo has been checked.";
  if (!isHexColor(f.brandColor)) e.brandColor = "Choose a colour like #2851D9.";
  if (chars(f.signTitle) > STORE_SETUP_LIMITS.signTitle) e.signTitle = `Use at most ${STORE_SETUP_LIMITS.signTitle} characters.`;
  if (chars(f.signMessage) > STORE_SETUP_LIMITS.signMessage) e.signMessage = `Use at most ${STORE_SETUP_LIMITS.signMessage} characters.`;
  if (ctx.hasSign && !f.signTitle.trim() && !f.signMessage.trim()) e.signTitle = "Write a title for the sign inside your store.";
  return e;
}

export const hasStoreErrors = (e: StoreFormErrors): boolean => Object.keys(e).length > 0;

/** The PUT body (null while the form has errors). `logoUploadId` is the new upload when one is ready, else the kept logo. */
export function toStoreRequest(f: StoreSetupForm, view: Pick<StoreSetupView, "sign" | "interiorTheme">, ctx: StoreFormContext & { newLogoUploadId: string | null }): StoreSetupRequest | null {
  if (hasStoreErrors(validateStoreForm(f, ctx)) || !f.category) return null;
  const site = checkDestinationUrl(f.website);
  return {
    businessName: f.businessName.trim(),
    description: f.description.trim(),
    website: site.ok && !site.empty ? site.normalized : null,
    category: f.category,
    logoUploadId: ctx.newLogoUploadId ?? f.logo?.uploadId ?? null,
    brandColor: f.brandColor.toUpperCase(),
    sign: { title: f.signTitle.trim(), message: f.signMessage.trim() },
    expectedSignVersion: view.sign.version,
    ...(f.interiorTheme !== view.interiorTheme ? { interiorTheme: f.interiorTheme } : {}),
    ...(f.reusedFrom ? { reuseCreativeId: f.reusedFrom.creativeId } : {}),
  };
}

/** True when the form differs from what was loaded (Save is offered either way: saving again is harmless). */
export function isStoreFormDirty(f: StoreSetupForm, base: StoreSetupForm, newLogo: boolean): boolean {
  return newLogo || JSON.stringify({ ...f, logo: f.logo?.uploadId ?? null }) !== JSON.stringify({ ...base, logo: base.logo?.uploadId ?? null });
}

/** The 1-3 character mark of a name-only sign (the server uses the same rule). */
export function storeMonogramOf(name: string): string {
  return (suggestMonogram(name) || "T").slice(0, 3);
}

// ---------------------------------------------------------------- the one plain status

export type StoreStatusTone = "live" | "checking" | "waiting" | "needs_change" | "not_set_up";

export interface StoreStatusCopy {
  tone: StoreStatusTone;
  /** The status itself ("Live", "Checking your design… (usually under a minute)", ...). */
  title: string;
  /** One line about what the town shows meanwhile / what to do. */
  detail: string;
}

/**
 * A reviewer's note in store words: the automated check's notes start "Not approved by Townsy's automated review:" and
 * invite a resubmission "of the same design"; the screen says what to change and how to ask a person instead.
 */
export function plainReason(reason: string | null): string {
  let r = (reason ?? "").trim();
  r = r.replace(/^Not approved by Townsy['’]s automated review:\s*/i, "").replace(/^Not approved:\s*/i, "");
  r = r.replace(/submit the same design again unchanged and a person will review it/i, "save again without changes and a Townsy reviewer will look at it");
  r = r.replace(/\bnot approved\b/gi, "not accepted").replace(/\bapproved\b/gi, "accepted").replace(/\brejected\b/gi, "not accepted");
  r = r.replace(/\bcreatives?\b/gi, "design").replace(/\badvertiser profile\b/gi, "business");
  if (!r) return "A Townsy reviewer asked for a change. Check the name, logo, website and description, then save again.";
  return r.charAt(0).toUpperCase() + r.slice(1);
}

/** `where`: on the store screen itself, or in the store panel (where the form is one click away). */
export function storeStatusCopy(status: Pick<StoreSetupStatus, "state" | "reason" | "categoryPolicy" | "previousSignShowing">, where: "screen" | "panel" = "screen"): StoreStatusCopy {
  const meanwhile = status.previousSignShowing ? "Until then the town shows your current sign." : "Until then visitors see your name and “NEW”.";
  if (where === "panel" && status.state === "not_set_up") {
    return { tone: "not_set_up", title: "Not set up yet", detail: "Visitors see your name and “NEW” until you set it up." };
  }
  if (where === "panel" && status.state === "needs_change") {
    return { tone: "needs_change", title: `Needs a change: ${plainReason(status.reason)}`, detail: "Open Edit your store, fix it and save again." };
  }
  switch (status.state) {
    case "live":
      return { tone: "live", title: "Live", detail: "Everyone in town sees your name, logo and website." };
    case "checking":
      return { tone: "checking", title: "Checking your design… (usually under a minute)", detail: meanwhile };
    case "waiting":
      return {
        tone: "waiting",
        title: "Waiting for a Townsy reviewer",
        detail: status.categoryPolicy
          ? "Townsy doesn't show signs in this category yet; a reviewer decides once the policy allows it. " + meanwhile
          : meanwhile,
      };
    case "needs_change":
      return {
        tone: "needs_change",
        title: `Needs a change: ${plainReason(status.reason)}`,
        detail: `Fix it below and save again. ${status.previousSignShowing ? "The town still shows your current sign." : "Until then visitors see your name and “NEW”."}`,
      };
    case "not_set_up":
      return { tone: "not_set_up", title: "Not set up yet", detail: "Visitors see your name and “NEW”. Add your business below and save." };
  }
}

/** What the Save answer says (what changed at once, what goes live after the check). */
export function savedMessage(saved: { design: "submitted" | "unchanged" | "applied"; sign: boolean; interior: boolean }, status: StoreSetupStatus): string {
  const now: string[] = [];
  if (saved.sign) now.push("the sign inside");
  if (saved.interior) now.push("the interior");
  const atOnce = now.length ? `Saved. ${now.join(" and ").replace(/^./, (c) => c.toUpperCase())} changed for everyone right away.` : "Saved.";
  // ADR-0031: the owner's own design, already checked, went on the store at once.
  if (saved.design === "applied") return `${atOnce} Your store is live: everyone in town sees your name, logo and website.`;
  if (saved.design === "submitted") {
    if (status.state === "live") return `${atOnce} Your store is live.`;
    return status.state === "checking"
      ? `${atOnce} Your name, logo and website go live as soon as the quick check is done (usually under a minute).`
      : `${atOnce} Your name, logo and website go live once a Townsy reviewer has looked at them.`;
  }
  return now.length ? atOnce : "Saved. Nothing else changed.";
}

// ---------------------------------------------------------------- the logo

export function logoStatusText(s: UploadState, kept: boolean): string {
  switch (s.phase) {
    case "idle":
    case "canceled":
      return kept ? "Your logo. Drop a new image here or choose a file to replace it." : "Drop your logo here, or choose a file. PNG, JPEG or WebP, up to 2 MB.";
    case "checking":
      return "Checking the file…";
    case "requesting":
    case "uploading":
      return `Uploading… ${Math.round(s.progress * 100)}%`;
    case "finalizing":
    case "processing":
      return "Preparing your logo (Townsy removes hidden data from the image)…";
    case "ready":
      return "Logo ready. Save to put it on your store.";
    case "rejected":
    case "rejected_local":
    case "expired":
    case "failed":
      return s.message ?? "The image could not be used. Try another file.";
  }
}

// ---------------------------------------------------------------- server refusals in store words

export function storeSaveErrorMessage(err: unknown): string {
  if (!isApiError(err)) return "Couldn't reach Townsy. Check your connection and try again.";
  const msg = err.message ?? "";
  switch (err.code) {
    case "UNAUTHENTICATED":
      return "Sign in again to save your store.";
    case "EMAIL_NOT_VERIFIED":
      return "Verify your email address first (see your Account page), then save again.";
    case "FORBIDDEN":
      return msg && !/creative|advertiser profile/i.test(msg) ? msg : "Only the current owner of this store can change it.";
    case "PROPERTY_VERSION_CHANGED":
      return msg || "Something on this store changed meanwhile. Reload, then save again.";
    case "UPLOAD_REJECTED":
      return msg || "Your logo could not be used. Upload it again.";
    case "RATE_LIMITED":
      return /waiting/i.test(msg) ? msg : "Too many saves in a short time. Wait a minute, then try again.";
    case "IDEMPOTENCY_CONFLICT":
      return "Your last save is still being processed. Try again in a moment.";
    case "VALIDATION_FAILED":
      return msg && !/^Invalid request|creative/i.test(msg) ? msg : "Check the highlighted fields, then save again.";
    case "NOT_FOUND":
      return "This store could not be found.";
    case "PROPERTY_TYPE_NOT_ELIGIBLE":
      return "Only stores can be set up here.";
    default:
      return "Townsy couldn't save your store right now. Try again in a moment.";
  }
}

/** Why the screen cannot open (sign in, not the owner, not found), from the read's error. */
export function storeLoadErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    if (err.status === 401) return "Sign in with the account that owns this store to set it up.";
    if (err.status === 403) return "Only the current owner of this store can set it up. If it was taken over, it belongs to someone else now.";
    if (err.status === 404) return "This store could not be found.";
    if (err.status === 422) return "Only stores can be set up here.";
  }
  return "Couldn't load your store. Check your connection and try again.";
}

/** The words the owner must never read on this screen (ADR-0020, founder feedback 2026-10-14). Used by the tests. */
export const STORE_SCREEN_FORBIDDEN_WORDS = [/\bapproved\b/i, /\brejected\b/i, /\bcreatives?\b/i, /\bplacements?\b/i, /\badvertiser profile\b/i] as const;
